import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState, useRef } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Button from '@mui/material/Button';

const commonStyles = {
  padding: '20px',
  width: '20vw',
};

export default function ChatRoom({
  isDrawerOpen,
  setIsDrawerOpen,
  socket,
  loggedInUsers,
  selectedUser,
  setSelectedUser,
  socketMessages,
  setSocketMessages,
  unreadMessages,
  clearUnreadMessages,
}) {
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, [selectedUser]);
  const [message, setMessage] = useState('');
  const handleClick = (e) => {
    e.preventDefault();
    if (!message.trim() || !socket.connection) return;
    socket.connection.emit(
      'chat-message',
      JSON.stringify({
        content: {
          recipientUserID: selectedUser.userID,
          message,
        },
        senderName: socket.connectionName,
        senderID: socket.connectionID,
      }),
    );
    setSocketMessages((prev) => {
      if (!(selectedUser.userID in prev)) {
        return {
          ...prev,
          [selectedUser.userID]: [
            {
              senderName: socket.connectionName,
              senderID: socket.connectionID,
              message,
            },
          ],
        };
      } else {
        return {
          ...prev,
          [selectedUser.userID]: [
            ...prev[selectedUser.userID],
            {
              senderName: socket.connectionName,
              senderID: socket.connectionID,
              message,
            },
          ],
        };
      }
    });
    setMessage('');
  };
  const renderLoggedInUsers = () => {
    if (!loggedInUsers.length) {
      return <p>You're the only one logged in right now</p>;
    }
    return loggedInUsers.map(({ userID, userName, organization }) => {
      return (
        <Box
          component='li'
          sx={{
            cursor: 'pointer',
            backgroundColor:
              selectedUser?.userID === userID ? 'rgba(0, 0, 0, 0.15)' : null,
            display: 'flex',
            flexDirection: 'row',
            gap: '5px',
          }}
          key={userID}
          onClick={() => {
            setSelectedUser((prev) =>
              prev?.userID === userID ? null : { userID, userName },
            );
            clearUnreadMessages(userID);
          }}
        >
          <Tooltip
            title={organization}
            placement='right'
          >
            <span>🟢 {userName}</span>
          </Tooltip>
          {unreadMessages[userID] ? (
            <Box
              component='span'
              sx={{
                minWidth: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: '#0b6efd',
                color: 'white',
                fontSize: 12,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unreadMessages[userID]}
            </Box>
          ) : null}
        </Box>
      );
    });
  };
  const renderUserChat = () => {
    const currentMessages = socketMessages[selectedUser.userID];
    const isMessages = currentMessages && currentMessages.length > 0;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: ' column',
          padding: '20px',
          gap: '15px',
          height: '100%',
          ...commonStyles,
        }}
      >
        <ArrowBackIosIcon
          sx={{ cursor: 'pointer' }}
          onClick={() => setSelectedUser(null)}
        />
        <strong>Chatting with {selectedUser.userName}</strong>
        {isMessages ? (
          <Box
            component='ul'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
              gap: '0.5rem',
              padding: '0.5rem',
              overflowWrap: 'anywhere',
            }}
          >
            {currentMessages.map(({ senderName, senderID, message }, idx) => {
              const direction =
                senderID === socket.connectionID ? 'to' : 'from';
              return (
                <Box
                  component='li'
                  sx={{
                    listStyleType: 'none',
                    maxWidth: '70%',
                    padding: ' 0.75rem 1rem',
                    borderRadius: '1rem',
                    alignSelf: direction === 'from' ? 'flex-start' : 'flex-end',
                    backgroundColor:
                      direction === 'from' ? '#e5e7eb' : '#3b82f6',
                    color: direction === 'to' ? 'white' : null,
                  }}
                  key={senderID.concat(idx)}
                >
                  {message}
                </Box>
              );
            })}
          </Box>
        ) : (
          <div className='no-messages'>
            <Box
              component='p'
              sx={{ margin: 0 }}
            >
              Send a message to start the conversation!
            </Box>
          </div>
        )}
        <Box
          sx={{
            marginTop: 'auto',
            padding: '0.25rem',
            display: 'flex',
            height: '3rem',
            gap: '10px',
          }}
        >
          <Box
            component='input'
            ref={inputRef}
            sx={{
              border: '2px solid black',
              padding: '0.6rem 1rem',
              borderRadius: '2rem',
              flex: 1,
              '&:focus': {
                outline: 'none',
              },
            }}
            id='input'
            placeholder='Do not add PHI.'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoComplete='off'
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleClick(e);
              }
            }}
          />
          <Button
            sx={{ borderRadius: '2rem' }}
            variant='contained'
            color='primary'
            onClick={handleClick}
          >
            Send
          </Button>
        </Box>
      </Box>
    );
  };
  return (
    <Box>
      <SwipeableDrawer
        anchor='left'
        open={isDrawerOpen}
        onOpen={() => setIsDrawerOpen(true)}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUser(null);
        }}
      >
        {selectedUser ? (
          renderUserChat()
        ) : (
          <Box sx={{ ...commonStyles }}>
            <strong>Select someone to chat with:</strong>
            <Box
              component='ul'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
              }}
            >
              <Box
                component='li'
                sx={{ listStyleType: 'none' }}
              ></Box>
              {renderLoggedInUsers()}
            </Box>
          </Box>
        )}
      </SwipeableDrawer>
    </Box>
  );
}
